import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchServerSentEvents, useChat } from '@tanstack/ai-react';
import { buildRequestHeaders } from '../lib/networking';

export type { UIMessage as ChatMessageData } from '@tanstack/ai-react';

export type RhinoBrowserTools = Record<
  string,
  (
    input: unknown,
    context: { signal: AbortSignal }
  ) => unknown | Promise<unknown>
>;

export interface RhinoChatOptions {
  assistant: string;
  ownerId: number;
  context?: Record<string, string | number | null>;
  browserTools?: RhinoBrowserTools;
}

// Mount with a key containing assistant + ownerId: conversations must never
// carry over when the user switches organizations.
export const useRhinoChat = ({
  assistant,
  ownerId,
  context,
  browserTools
}: RhinoChatOptions) => {
  const [progress, setProgress] = useState<string | null>(null);
  const contextRef = useRef(context);
  const handlersRef = useRef(browserTools);
  const continuationRef = useRef<string | undefined>(undefined);
  const executionRef = useRef(new AbortController());
  useEffect(() => {
    handlersRef.current = browserTools;
  }, [browserTools]);
  const tools = Object.keys(browserTools ?? {}).map((name) => ({
    name,
    description: name,
    __toolSide: 'client' as const,
    execute: async (input: unknown) => {
      // TanStack invokes execute after receiving a tool call, never during render.
      // eslint-disable-next-line react-hooks/refs
      const signal = executionRef.current.signal;
      signal.throwIfAborted();
      if (!continuationRef.current)
        throw new Error('Browser action was not authorized.');
      // eslint-disable-next-line react-hooks/refs
      const handler = handlersRef.current?.[name];
      if (!handler) throw new Error('Browser action is unavailable.');
      setProgress(name);
      try {
        const result = await handler(input, { signal });
        signal.throwIfAborted();
        setProgress('preparing_response');
        return result;
      } catch (error) {
        if (!signal.aborted) setProgress(null);
        throw error;
      }
    }
  }));
  useEffect(() => {
    contextRef.current = context;
  }, [context]);
  const connection = useMemo(
    () =>
      fetchServerSentEvents(
        `/api/ai/chat/${encodeURIComponent(assistant)}`,
        // The adapter invokes this callback when sending a request, never during render.
        // eslint-disable-next-line react-hooks/refs
        () => {
          const continuation = continuationRef.current;
          continuationRef.current = undefined;
          return {
            headers: buildRequestHeaders({ Accept: 'text/event-stream' }),
            body: {
              owner_id: ownerId,
              ...(continuation && { continuation }),
              ...(contextRef.current && { context: contextRef.current })
            },
            credentials: 'same-origin'
          };
        }
      ),
    [assistant, ownerId]
  );

  const chat = useChat({
    connection,
    tools,
    onChunk: (chunk) => {
      if (executionRef.current.signal.aborted) return;
      if (chunk.type === 'STEP_STARTED') setProgress(chunk.stepName);
      if (chunk.type === 'STEP_FINISHED')
        setProgress((current) => (current === chunk.stepName ? null : current));
      if (chunk.type === 'RUN_FINISHED' || chunk.type === 'RUN_ERROR')
        setProgress(null);
    },
    onError: () => setProgress(null),
    onCustomEvent: (name, value) => {
      if (name === 'rhino.browser_continuation' && typeof value === 'string') {
        continuationRef.current = value;
      }
    }
  });
  const stopChat = chat.stop;
  useEffect(() => {
    executionRef.current = new AbortController();
    return () => {
      executionRef.current.abort();
      continuationRef.current = undefined;
      // TanStack defers disposal for Strict Mode. Stop immediately so a pending
      // browser result cannot resume the old organization during that window.
      stopChat();
    };
  }, [stopChat]);
  const resetExecution = () => {
    setProgress(null);
    executionRef.current.abort();
    executionRef.current = new AbortController();
    continuationRef.current = undefined;
  };
  return {
    ...chat,
    progress: chat.isLoading ? progress : null,
    sendMessage: (...args: Parameters<typeof chat.sendMessage>) => {
      resetExecution();
      setProgress('preparing_response');
      return chat.sendMessage(...args);
    },
    reload: () => {
      resetExecution();
      setProgress('preparing_response');
      return chat.reload();
    },
    stop: () => {
      executionRef.current.abort();
      continuationRef.current = undefined;
      setProgress(null);
      chat.stop();
    },
    clear: () => {
      resetExecution();
      chat.clear();
    }
  };
};
